import {
  type IPCEventType,
  type IPCEventTypeToInferredPayloadMap,
} from '@electron/shared';
import { validateContextKitResponse } from '@electron/shared/utils';

import type { PushChannel } from '@electron/shared/ipc-channels';

// Overload for specifying just the return type T
export function listenWithAbortIPC<K extends IPCEventType>(
  messageType: K,
  callback: (
    payload: K extends keyof IPCEventTypeToInferredPayloadMap
      ? IPCEventTypeToInferredPayloadMap[K]
      : unknown
  ) => void,
  signal?: AbortSignal
): () => void;
// eslint-disable-next-line no-redeclare
export function listenWithAbortIPC<T>(
  messageType: PushChannel,
  callback: (payload: T) => void,
  signal?: AbortSignal
): () => void;
// eslint-disable-next-line no-redeclare
export function listenWithAbortIPC<
  K extends PushChannel,
  T = K extends keyof IPCEventTypeToInferredPayloadMap
    ? IPCEventTypeToInferredPayloadMap[K]
    : unknown,
>(
  messageType: K,
  callback: (payload: T) => void,
  signal?: AbortSignal
): (() => void) | undefined {
  if (signal?.aborted) {
    return;
  }

  if (!window.ipcRenderer) {
    console.warn(
      'IPC Renderer not available. Running in non-Electron environment?'
    );
    return;
  }

  const eventHandler = (_event: unknown, response: T) => {
    if (signal?.aborted) {
      return;
    }

    const validatedResponse = validateContextKitResponse(messageType, response);

    callback(validatedResponse as T);
  };

  const unsubscribe = window.ipcRenderer.on(messageType, eventHandler);

  signal?.addEventListener('abort', unsubscribe, { once: true });

  return () => {
    signal?.removeEventListener('abort', unsubscribe);
    unsubscribe();
  };
}
